import vuetifyColors from "vuetify/lib/util/colors";

/**
 * Converts a camelCase string to kebab-case.
 *
 * @param {*} str
 * @return {*}
 */
function camelToKebab(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * Converts a kebab-case string to camelCase.
 *
 * @param {*} str
 * @return {*}
 */
function kebabToCamel(str) {
  return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
}

/**
 * Normalizes a hex color code by removing the trailing "ff" if it exists and the hex code is 8 digits long.
 *
 * @param {*} hex
 * @return {*}
 */
function normalizeHex(hex) {
  hex = hex.toLowerCase();
  // Remove trailing "ff" if present and hex is 8 digits
  if (/^#([0-9a-f]{8})$/.test(hex) && hex.endsWith("ff")) {
    return "#" + hex.slice(1, 7);
  }
  return hex;
}

/**
 * Retrieves a Vuetify material color value or its name based on a given color hex code or color name.
 *
 * @param {string} color - The hex color value (e.g., "#1976d2") or color name (e.g., "red").
 * @param {Object} [options] - Optional settings for color retrieval.
 * @param {string} [options.type] - The shade type to retrieve ("base", "lighten-1", "darken-2", "accent-1", etc.).
 * @param {string} [options.output] - The output format: "hex" for hex value, "word" for color name and shade.
 * @returns {string} The corresponding Vuetify color hex value or color name/shade, or an empty string if not found or invalid input.
 */
export function getMaterialColor(color, options) {
  try {
    if (!color || typeof color !== "string") {
      throw new Error("Invalid color value");
    }
    const colors = vuetifyColors;
    color = color.toLowerCase();
    const type = options?.type?.toLowerCase?.() ?? "base";
    const output = options?.output?.toLowerCase?.() ?? "hex";

    let vuetifyShade = "base";
    if (typeof type !== "string") {
      throw new Error("Invalid type option");
    }
    if (type === "base") {
      vuetifyShade = "base";
    } else if (type.startsWith("lighten-")) {
      vuetifyShade = "lighten" + type.split("-")[1];
    } else if (type.startsWith("darken-")) {
      vuetifyShade = "darken" + type.split("-")[1];
    } else if (type.startsWith("accent-")) {
      vuetifyShade = "accent" + type.split("-")[1];
    }

    // Try to detect if input is a color name (with optional shade) or a hex value
    // If it matches a hex code pattern, treat as hex
    const isHex = /^#([0-9a-f]{3,8})$/i.test(color);

    let colorKey = null;
    let shadeKey = vuetifyShade;

    if (!isHex) {
      // Try to parse as "colorName shade" or just "colorName"
      const parts = color.trim().split(/\s+/);
      // Convert color name to camelCase for Vuetify matching
      colorKey = kebabToCamel(parts[0]);
      if (!colors[colorKey]) {
        // If not found as color name, try as hex anyway
        // (e.g. user might have given "1976d2" without #)
        if (/^[0-9a-f]{3,8}$/i.test(color)) {
          color = "#" + color;
          return getMaterialColor(color, options);
        }
        return "";
      }

      if (parts.length > 1) {
        const shadePart = parts[1];
        if (shadePart === "base") {
          shadeKey = "base";
        } else if (shadePart.startsWith("lighten-")) {
          shadeKey = "lighten" + shadePart.split("-")[1];
        } else if (shadePart.startsWith("darken-")) {
          shadeKey = "darken" + shadePart.split("-")[1];
        } else if (shadePart.startsWith("accent-")) {
          shadeKey = "accent" + shadePart.split("-")[1];
        }
      }
      const colorObj = colors[colorKey];
      if (!colorObj || typeof colorObj !== "object") return "";
      const hexValue = colorObj[shadeKey] || colorObj.base;
      if (!hexValue) return "";

      if (output === "hex") {
        return hexValue;
      } else if (output === "word") {
        const kebabColorKey = camelToKebab(colorKey);
        if (shadeKey === "base") {
          return kebabColorKey;
        } else {
          const wordShade = shadeKey.replace(/([a-z]+)(\d)/, "$1-$2");
          return `${kebabColorKey} ${wordShade}`;
        }
      }
      return "";
    } else {
      // Normalize hex for matching (remove trailing "ff" if present)
      const normalizedColor = normalizeHex(color);

      // Try to find color by hex value
      for (const [key, value] of Object.entries(colors)) {
        if (typeof value === "object" && value !== null) {
          for (const [shade, hex] of Object.entries(value)) {
            if (
              typeof hex === "string" &&
              (hex.toLowerCase() === normalizedColor ||
                normalizeHex(hex) === normalizedColor)
            ) {
              colorKey = key;
              shadeKey = shade;
              break;
            }
          }
        } else if (typeof value === "string") {
          if (
            value.toLowerCase() === normalizedColor ||
            normalizeHex(value) === normalizedColor
          ) {
            colorKey = key;
            shadeKey = "base";
            break;
          }
        }
        if (colorKey) break;
      }

      if (!colorKey) return "";

      const colorObj = colors[colorKey];
      if (!colorObj || typeof colorObj !== "object") {
        throw new Error("Color object not found");
      }
      const hexValue = colorObj[vuetifyShade] || colorObj.base;
      if (!hexValue) {
        throw new Error("Hex value not found for the specified shade");
      }

      if (output === "hex") {
        return hexValue;
      } else if (output === "word") {
        const kebabColorKey = camelToKebab(colorKey);
        if (vuetifyShade === "base") {
          return kebabColorKey;
        } else {
          const wordShade = vuetifyShade.replace(/([a-z]+)(\d)/, "$1-$2");
          return `${kebabColorKey} ${wordShade}`;
        }
      }
      return "";
    }
  } catch (error) {
    return "";
  }
}
